<template>
    <a-config-provider :locale="ptBR">
        <a-spin :spinning="loading" tip="Carregando auditoria cadastral...">
            <div class="cadastral-audit">
                <fieldset v-if="property" class="property-panel">
                    <legend>Dados cadastrais do imóvel</legend>
                    <a-descriptions bordered :column="1" size="small">
                        <a-descriptions-item label="Matrícula">{{ property.matricula }}</a-descriptions-item>
                        <a-descriptions-item v-if="property.idbql" label="IDBQL">{{ property.idbql }}</a-descriptions-item>
                        <a-descriptions-item v-if="property.setor" label="Setor">{{ property.setor }}</a-descriptions-item>
                        <a-descriptions-item v-if="property.quadra" label="Quadra">{{ property.quadra }}</a-descriptions-item>
                        <a-descriptions-item v-if="property.lote" label="Lote">{{ property.lote }}</a-descriptions-item>
                        <a-descriptions-item v-if="property.logradouro" label="Endereço">
                            {{ property.logradouro }}{{ property.numero ? ' - ' + property.numero : '' }}{{ property.bairro ? '/' + property.bairro : '' }}
                        </a-descriptions-item>
                        <a-descriptions-item v-if="property.proprietario" label="Proprietário">{{ property.proprietario }}</a-descriptions-item>
                    </a-descriptions>
                </fieldset>

                <div class="map-container">
                    <div ref="mapEl" class="map-viewport"></div>
                    <MapLayerSwitcher :map="mapRef" v-if="mapRef" />
                </div>
            </div>
        </a-spin>
    </a-config-provider>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import axios from 'axios'
import ptBR from 'ant-design-vue/es/locale/pt_BR'
import 'ol/ol.css'
import 'ol-ext/dist/ol-ext.css'
import Map from 'ol/Map'
import View from 'ol/View'
import GeoJSON from 'ol/format/GeoJSON'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Text from 'ol/style/Text'
import { defaults as defaultControlsOL } from 'ol/control/defaults'
import { fromLonLat } from 'ol/proj'
import { baseLayers } from '../../../../open-layers/baseLayers'
import { fullScreenBtn, scaleLine } from '../../../../open-layers/default-controls'
import { geoJsonReadOptions } from '../../../../open-layers/projections.js'
import MapLayerSwitcher from '../../MapsOpenLayers/Components/MapLayerSwitcher.vue'

const AUDIT_MAP_ZOOM = 16

const props = defineProps({
    matricula: { type: [Number, String], required: true },
})

const loading = ref(true)
const property = ref(null)
const mapEl = ref(null)
const mapRef = ref(null)
let mapInstance = null

const readFeatures = (geojson) => {
    const normalized = normalizeGeoJson(geojson)
    if (!normalized.features.length) {
        return []
    }
    return new GeoJSON().readFeatures(normalized, geoJsonReadOptions())
}

const normalizeGeoJson = (geojson) => {
    if (!geojson) {
        return { type: 'FeatureCollection', features: [] }
    }
    if (geojson.type === 'FeatureCollection') {
        return geojson
    }
    if (geojson.type === 'Feature') {
        return { type: 'FeatureCollection', features: [geojson] }
    }
    if (geojson.type === 'GeometryCollection' && Array.isArray(geojson.geometries)) {
        return {
            type: 'FeatureCollection',
            features: geojson.geometries.map((geometry) => ({
                type: 'Feature',
                geometry,
                properties: {},
            })),
        }
    }
    return {
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: geojson, properties: {} }],
    }
}

const segmentStyle = (strokeColor, getLabel) => (feature) => new Style({
    text: new Text({
        text: getLabel(feature),
        font: 'bold 11px sans-serif',
        stroke: new Stroke({ width: 3, color: '#ffffff' }),
    }),
    stroke: new Stroke({ width: 4, color: strokeColor }),
    fill: new Fill({ color: `rgba(${strokeColor[0]}, ${strokeColor[1]}, ${strokeColor[2]}, 0.3)` }),
})

const neighborStyle = (feature) => new Style({
    text: new Text({
        text: feature.get('label') || feature.get('matricula') || '',
        font: 'bold 11px sans-serif',
        stroke: new Stroke({ width: 1, color: '#ffffff' }),
    }),
    stroke: new Stroke({ width: 4, color: [255, 0, 0] }),
    fill: new Fill({ color: [255, 128, 0, 0.3] }),
})

const colorFromLabel = (label) => {
    let hash = 0
    for (let i = 0; i < label.length; i += 1) {
        hash = label.charCodeAt(i) + ((hash << 5) - hash)
    }
    const color = (hash & 0x00ffffff).toString(16).padStart(6, '0')

    return `#${color}`
}

const auditLayerStyle = (layerName) => {
    const color = colorFromLabel(layerName)
    const dateLabel = layerName.includes(':') ? layerName.split(': ').slice(1).join(': ') : layerName

    return () => new Style({
        stroke: new Stroke({ color, width: 8 }),
        fill: new Fill({ color: `${color}55` }),
        text: new Text({
            text: dateLabel,
            font: 'bold 14px sans-serif',
            stroke: new Stroke({ width: 1, color: 'rgba(55,255,255,0.6)' }),
            fill: new Fill({ color: 'rgba(255,255,255,0.9)' }),
        }),
    })
}

const vectorLayerFromGeoJson = (geojson, options) => {
    const features = readFeatures(geojson)
    if (!features.length && !options.allowEmpty) {
        return null
    }

    const layer = new VectorLayer({
        source: new VectorSource({ features }),
        title: options.title,
        visible: options.visible !== false,
        style: options.style,
        properties: {
            displayInLayerSwitcher: options.displayInLayerSwitcher !== false,
            geo_table: options.geoTable,
        },
    })

    layer.set('name', options.title)
    layer.set('title', options.title)

    return layer
}

const buildAuditLayers = (layers, geoTable) => {
    return layers
        .filter((layerData) => !!layerData?.geojson)
        .map((layerData) => vectorLayerFromGeoJson(layerData.geojson, {
            title: layerData.nome,
            visible: !!layerData.active,
            style: auditLayerStyle(layerData.nome),
            geoTable,
        }))
        .filter(Boolean)
}

const initMap = (mapData) => {
    const center = fromLonLat(mapData.center || [-50.14837285, -30.03971932])

    const loteLayer = vectorLayerFromGeoJson(mapData.lote_segments, {
        title: 'Lote',
        style: segmentStyle([255, 128, 0], (f) => f.get('medida') || ''),
        allowEmpty: true,
        geoTable: 'public.geo_lote',
    })

    const quadraLayer = vectorLayerFromGeoJson(mapData.quadra_segments, {
        title: 'Quadra',
        style: segmentStyle([0, 255, 255], (f) => f.get('medida') || ''),
        allowEmpty: true,
        geoTable: 'public.geo_quadras',
    })

    const neighborsLayer = vectorLayerFromGeoJson(mapData.neighboring_lots, {
        title: 'Lotes lindeiros',
        style: neighborStyle,
        allowEmpty: true,
        geoTable: 'public.geo_lote',
    })

    const loteAuditLayers = buildAuditLayers(mapData.lote_audit_layers || [], 'public.geo_lote_audit')
    const edificacaoAuditLayers = buildAuditLayers(mapData.edificacao_audit_layers || [], 'public.geo_edificacao_audit')

    const mapLayers = [baseLayers()]
    mapLayers.push(...loteAuditLayers)
    mapLayers.push(...edificacaoAuditLayers)
    if (neighborsLayer) mapLayers.push(neighborsLayer)
    if (quadraLayer) mapLayers.push(quadraLayer)
    if (loteLayer) mapLayers.push(loteLayer)

    mapInstance = new Map({
        target: mapEl.value,
        layers: mapLayers,
        controls: defaultControlsOL({ attribution: false }).extend([
            fullScreenBtn,
            scaleLine,
        ]),
        view: new View({
            center,
            zoom: AUDIT_MAP_ZOOM,
            minZoom: 5,
            maxZoom: 22,
        }),
    })
    mapRef.value = mapInstance
}

const loadData = async () => {
    loading.value = true
    try {
        const { data } = await axios.get(`/api/map-actions/property-cadastral-audit/${props.matricula}`)
        property.value = data?.data?.property ?? null
        initMap(data?.data?.map ?? {})
    } finally {
        loading.value = false
    }
}

onMounted(loadData)

onUnmounted(() => {
    mapInstance?.setTarget(null)
    mapInstance = null
    mapRef.value = null
})
</script>

<style scoped>
.cadastral-audit {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    height: 100vh;
    box-sizing: border-box;
}

.property-panel {
    max-width: 900px;
    margin: 0 auto;
    width: 100%;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    padding: 12px 16px;
}

.property-panel legend {
    font-weight: 600;
    padding: 0 8px;
}

.map-container {
    flex: 1;
    min-height: 400px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    overflow: hidden;
    position: relative;
}

.map-viewport {
    width: 100%;
    height: 100%;
}
</style>
